import { useMobile } from "@packages/hooks/useMobile";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

interface CustomBubbleProps {
  children: React.ReactNode;
  isEnhancing?: boolean;
  actionButtons?: React.ReactNode;
}

export const CustomBubble = ({
  children,
  isEnhancing = false,
  actionButtons,
}: CustomBubbleProps) => {
  const isMobile = useMobile();
  const [showScrollButton, setShowScrollButton] = useState(true);
  const [hasGeneratedImage, setHasGeneratedImage] = useState(false);

  // Check if image has been generated by looking for specific elements in the DOM
  useEffect(() => {
    // This checks for elements that would indicate an image was generated
    const checkForGeneratedImage = () => {
      // Check for any active image generation or completed generations
      const generatedImageElements = document.querySelectorAll(
        '[data-testid="imagen-prompt-input"] + div',
      );
      const isGeneratingElements = document.querySelectorAll(
        '.isGenerating, [data-testid="generate-images-button"] .animate-spin',
      );
      const showPreviewElements = document.querySelectorAll(".showPreview");

      setHasGeneratedImage(
        generatedImageElements.length > 0 ||
          isGeneratingElements.length > 0 ||
          showPreviewElements.length > 0,
      );
    };

    checkForGeneratedImage();

    // Set up a mutation observer to detect when the DOM changes
    const observer = new MutationObserver(checkForGeneratedImage);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => observer.disconnect();
  }, []);

  // Check for image generation status and loading state
  useEffect(() => {
    const checkGenerationStatus = () => {
      // Check for image generation in progress
      const isGenerating =
        document.querySelector(
          '[data-testid="generate-images-button"] .animate-spin',
        ) !== null;

      // Check for completed image generation
      const hasImage =
        document.querySelector('[data-testid="imagen-prompt-input"] + div') !==
        null;

      // Set generated image state based on checks
      setHasGeneratedImage(hasImage || isGenerating);
    };

    // Initial check
    checkGenerationStatus();

    // Set up observer to detect DOM changes
    const observer = new MutationObserver(checkGenerationStatus);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => observer.disconnect();
  }, []);

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = 100; // Adjust this value based on when you want the button to appear/disappear

      // Show button only when scrolled to top and no image has been generated
      setShowScrollButton(scrollY < threshold && !hasGeneratedImage);
    };

    // Initial check
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasGeneratedImage]);

  // Function to scroll to gallery section
  const scrollToGallery = () => {
    // Find the gallery element or use a fixed scroll position
    const galleryElement = document.querySelector(".gallery-section") || null;

    if (galleryElement) {
      galleryElement.scrollIntoView({ behavior: "smooth" });
    } else {
      // If no specific element found, scroll down a reasonable amount
      window.scrollTo({
        top: window.innerHeight,
        behavior: "smooth",
      });
    }

    // Hide the button after clicking
    setShowScrollButton(false);
  };

  if (isMobile) {
    return (
      <div className="relative w-[92vw] bg-white dark:bg-gray-800 rounded-xl p-4">
        {children}
        {actionButtons && (
          <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            {actionButtons}
          </div>
        )}
      </div>
    );
  }

  return (
    <motion.div
      key="creation"
      layout
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -30 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative w-full"
    >
      {/* Bulut boyutunu artırdık - daha yüksek bir aspect ratio */}
      <motion.div
        className="w-full h-[60vh]" // Aspect ratio'yu daha yüksek yaptık
        initial={{ scale: 0.9, opacity: 0.8 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Background rectangle - genişlik ve yüksekliği arttırıldı */}
        <motion.div
          className="absolute bg-white dark:bg-gray-800"
          style={{
            left: "12.17%",
            top: "15.23%", // Yukarıya doğru daha fazla yer
            width: "73.05%",
            height: "77.07%", // Daha fazla yükseklik
          }}
          initial={{ opacity: 0.8 }}
          animate={
            isEnhancing ? { opacity: 0.8 } : { opacity: [0.8, 0.9, 0.8] }
          }
          transition={{
            duration: 3,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Tüm elipslerin boyutlarını ve konumlarını aynı oranda büyüttük */}
        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "6.9%",
            top: "12.1%",
            width: "13.1%",
            height: "23.9%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.08, 1] }}
          transition={{
            duration: 4,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 0.1,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "17.6%",
            top: "8.3%",
            width: "12.2%",
            height: "38.5%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.08, 1] }}
          transition={{
            duration: 5,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 0.2,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "27.5%",
            top: "0%",
            width: "22.2%",
            height: "38.5%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.15, 1] }}
          transition={{
            duration: 4.5,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 0.3,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "46.0%",
            top: "3.6%",
            width: "18.8%",
            height: "51.0%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.08, 1] }}
          transition={{
            duration: 6,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 0.4,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "57.2%",
            top: "1.1%",
            width: "24.8%",
            height: "41.0%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.12, 1] }}
          transition={{
            duration: 4.2,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "78.4%",
            top: "5.9%",
            width: "15.8%",
            height: "31.0%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.08, 1] }}
          transition={{
            duration: 5.5,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 0.6,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "0.8%",
            top: "24.1%",
            width: "22.2%",
            height: "38.5%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.12, 1] }}
          transition={{
            duration: 4.8,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 0.7,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "0%",
            top: "46.9%",
            width: "22.2%",
            height: "38.5%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.08, 1] }}
          transition={{
            duration: 5.2,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 0.8,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "4.4%",
            top: "48.4%",
            width: "25.7%",
            height: "48.0%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.15, 1] }}
          transition={{
            duration: 4.5,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 0.9,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "25.8%",
            top: "50.8%",
            width: "25.7%",
            height: "48.0%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.12, 1] }}
          transition={{
            duration: 4.3,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 1.1,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "78.7%",
            top: "25.6%",
            width: "21.3%",
            height: "38.3%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.12, 1] }}
          transition={{
            duration: 4.7,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 1.3,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "41.9%",
            top: "59.8%",
            width: "22.9%",
            height: "40.2%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.08, 1] }}
          transition={{
            duration: 5.5,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 1.4,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "58.3%",
            top: "57.1%",
            width: "22.9%",
            height: "40.2%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.12, 1] }}
          transition={{
            duration: 4.6,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 1.7,
          }}
        />

        <motion.div
          className="absolute bg-white dark:bg-gray-800 rounded-full"
          style={{
            left: "76.3%",
            top: "57.1%",
            width: "22.9%",
            height: "40.2%",
          }}
          animate={isEnhancing ? { scale: 1 } : { scale: [1, 1.08, 1] }}
          transition={{
            duration: 5.3,
            repeat: isEnhancing ? 0 : Infinity,
            ease: "easeInOut",
            delay: 1.8,
          }}
        />
      </motion.div>

      {/* SVG Mask Definition */}
      {isEnhancing && (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: -1 }}
        >
          <defs>
            <mask id="bubble-mask">
              {/* Background rectangle - güncellendi */}
              <rect
                x="12.17%"
                y="15.23%"
                width="73.05%"
                height="77.07%"
                fill="white"
              />
              {/* Tüm elips maskelerini güncelledik */}
              <ellipse
                cx="13.45%"
                cy="24.05%"
                rx="6.55%"
                ry="11.95%"
                fill="white"
              />
              <ellipse
                cx="23.7%"
                cy="27.05%"
                rx="6.1%"
                ry="19.25%"
                fill="white"
              />
              <ellipse
                cx="38.6%"
                cy="19.25%"
                rx="11.1%"
                ry="19.25%"
                fill="white"
              />
              <ellipse
                cx="55.4%"
                cy="29.1%"
                rx="9.4%"
                ry="25.5%"
                fill="white"
              />
              <ellipse
                cx="69.6%"
                cy="21.6%"
                rx="12.4%"
                ry="20.5%"
                fill="white"
              />
              <ellipse
                cx="86.3%"
                cy="21.4%"
                rx="7.9%"
                ry="15.5%"
                fill="white"
              />
              <ellipse
                cx="11.9%"
                cy="43.35%"
                rx="11.1%"
                ry="19.25%"
                fill="white"
              />
              <ellipse
                cx="11.1%"
                cy="66.15%"
                rx="11.1%"
                ry="19.25%"
                fill="white"
              />
              <ellipse
                cx="17.25%"
                cy="72.4%"
                rx="12.85%"
                ry="24%"
                fill="white"
              />
              <ellipse
                cx="38.65%"
                cy="74.8%"
                rx="12.85%"
                ry="24%"
                fill="white"
              />
              <ellipse
                cx="89.35%"
                cy="44.75%"
                rx="10.65%"
                ry="19.15%"
                fill="white"
              />
              <ellipse
                cx="53.35%"
                cy="80%"
                rx="11.45%"
                ry="20.1%"
                fill="white"
              />
              <ellipse
                cx="69.75%"
                cy="77.2%"
                rx="11.45%"
                ry="20.1%"
                fill="white"
              />
              <ellipse
                cx="87.75%"
                cy="77.2%"
                rx="11.45%"
                ry="20.1%"
                fill="white"
              />
            </mask>
          </defs>
        </svg>
      )}

      {/* Enhancement Animation Overlay */}
      {isEnhancing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 rounded-xl overflow-hidden z-20"
          style={{
            mask: "url(#bubble-mask)",
            WebkitMask: "url(#bubble-mask)",
          }}
        >
          {/* Glass effect overlay with enhanced blur */}
          <motion.div
            className="absolute inset-0 backdrop-blur-[12px]"
            animate={{
              background: [
                "radial-gradient(circle at center, rgba(255,255,255,0.1) 30%, rgba(255,255,255,0.05) 70%)",
                "radial-gradient(circle at center, rgba(255,255,255,0.05) 30%, rgba(255,255,255,0.1) 70%)",
              ],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Enhanced energetic shapes with more variety */}
          <motion.div
            className="absolute w-32 h-32 rounded-full bg-gradient-to-r from-fuchsia-500/40 to-purple-600/40 blur-xl"
            animate={{
              x: ["0%", "60%", "0%"],
              y: ["0%", "60%", "0%"],
              scale: [1, 1.8, 1],
              rotate: [0, 360, 0],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute w-40 h-40 rounded-full bg-gradient-to-r from-cyan-400/40 to-blue-600/40 blur-xl"
            animate={{
              x: ["80%", "20%", "80%"],
              y: ["20%", "80%", "20%"],
              scale: [1.5, 0.5, 1.5],
              rotate: [180, -180, 180],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="absolute w-36 h-36 rounded-full bg-gradient-to-r from-amber-400/40 to-orange-600/40 blur-xl"
            animate={{
              x: ["40%", "90%", "40%"],
              y: ["90%", "30%", "90%"],
              scale: [0.5, 2, 0.5],
              rotate: [45, 315, 45],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* More energetic particles with varied behaviors */}
          {[...Array(16)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute w-3 h-3 rounded-full ${
                [
                  "bg-pink-400/90",
                  "bg-purple-400/90",
                  "bg-blue-400/90",
                  "bg-green-400/90",
                  "bg-yellow-400/90",
                  "bg-orange-400/90",
                  "bg-red-400/90",
                  "bg-indigo-400/90",
                ][i % 8]
              }`}
              initial={{
                x: `${Math.random() * 100}%`,
                y: `${Math.random() * 100}%`,
                scale: 0,
              }}
              animate={{
                x: [
                  `${Math.random() * 100}%`,
                  `${Math.random() * 100}%`,
                  `${Math.random() * 100}%`,
                ],
                y: [
                  `${Math.random() * 100}%`,
                  `${Math.random() * 100}%`,
                  `${Math.random() * 100}%`,
                ],
                scale: [0, 1.8, 0],
                rotate: [0, 360, 720],
              }}
              transition={{
                duration: 5 + Math.random() * 2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.1,
              }}
            />
          ))}

          {/* Enhanced rainbow shimmer with multiple layers */}
          <motion.div
            className="absolute inset-0"
            animate={{
              background: [
                "linear-gradient(45deg, transparent, rgba(255,105,180,0.3), transparent)",
                "linear-gradient(135deg, transparent, rgba(147,112,219,0.3), transparent)",
                "linear-gradient(225deg, transparent, rgba(64,224,208,0.3), transparent)",
                "linear-gradient(315deg, transparent, rgba(255,215,0,0.3), transparent)",
              ],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          <motion.div
            className="absolute inset-0"
            animate={{
              x: ["-100%", "100%"],
              background: [
                "linear-gradient(to right, transparent, rgba(255,255,255,0.4), transparent)",
                "linear-gradient(to right, transparent, rgba(255,255,255,0.2), transparent)",
              ],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Pulsing center highlight */}
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-white/20"
            animate={{
              scale: [0, 1.5, 0],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </motion.div>
      )}

      <div className="absolute inset-0 flex flex-col items-center justify-start p-4 md:p-8 lg:p-10">
        <div className="w-[90%] mx-auto flex flex-col h-full">
          {/* Content with dynamic scroll area */}
          <div
            className="flex-grow w-full scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent flex flex-col justify-center"
            style={{
              maxHeight: actionButtons ? "calc(100% - 40px)" : "100%",
              minHeight: actionButtons ? "150px" : "200px",
            }}
          >
            {children}
          </div>

          {/* Action buttons outside of scroll area with minimal spacing */}
          {actionButtons && (
            <div className="w-full mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              {actionButtons}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
